import Vue from 'vue';
import { Video, type VideoCategory } from './models/Video';
import { type RequestState } from '../global/types/TechTypes';
import { BackEndService } from '../../utils/BackEndService';
import { skillService } from './SkillService';

interface VideoServiceState {
  videoCategories: VideoCategory[]
  videoCategoriesRequestState: RequestState
  videoCategoryById: Record<number, VideoCategory>
  videoCategoryByIdRequestState: Record<number, RequestState>
}

class VideoService {
  public readonly state = Vue.observable<VideoServiceState>({
    videoCategories: [],
    videoCategoriesRequestState: 'idle',
    videoCategoryById: {},
    videoCategoryByIdRequestState: {},
  })

  private videoCategoriesPromise: Promise<VideoCategory[]> | null = null;
  refreshVideoCategoriesState(): void {
    this.state.videoCategories = [];
    this.state.videoCategoriesRequestState = 'idle';
    this.videoCategoriesPromise = null;
  }
  async loadVideoCategories(force = false): Promise<VideoCategory[]> {
    if (this.state.videoCategoriesRequestState === 'loading' && this.videoCategoriesPromise) {
      return this.videoCategoriesPromise
    }

    if (this.state.videoCategoriesRequestState !== 'loaded' || force) {
      this.state.videoCategoriesRequestState = 'loading';
      this.videoCategoriesPromise = BackEndService.sendGetRequest('video-categories')
        .then(response => {
          this.state.videoCategories = response;
          this.state.videoCategoriesRequestState = 'loaded';
          return response;
        })
        .catch(error => {
          console.error('Error loading video categories:', error);
          this.state.videoCategoriesRequestState = 'error';
          throw error;
        });
    }

    if (this.state.videoCategoriesRequestState === 'loaded') {
      return this.state.videoCategories;
    }

    return Promise.resolve(this.state.videoCategories);
  }

  private videoCategoryByIdPromise: Record<number, Promise<VideoCategory> | undefined> = {};
  async loadVideoCategoryById(id: number, force = false): Promise<VideoCategory> {
    if (this.state.videoCategoryByIdRequestState[id] === 'loading' && this.videoCategoryByIdPromise[id]) {
      return this.videoCategoryByIdPromise[id];
    }

    if (this.state.videoCategoryByIdRequestState[id] !== 'loaded' || force) {
      this.state.videoCategoryByIdRequestState = { ...this.state.videoCategoryByIdRequestState, [id]: 'loading' };
      this.videoCategoryByIdPromise[id] = BackEndService.sendGetRequest(`video-categories/${id}`)
        .then(response => {
          this.state.videoCategoryById = { ...this.state.videoCategoryById, [id]: response };
          this.state.videoCategoryByIdRequestState = { ...this.state.videoCategoryByIdRequestState, [id]: 'loaded' };
          return response;
        })
        .catch(error => {
          console.error(`Error loading video category with ID ${id}:`, error);
          this.state.videoCategoryByIdRequestState = { ...this.state.videoCategoryByIdRequestState, [id]: 'error' };
          throw error;
        });
    }

    if (this.state.videoCategoryByIdRequestState[id] === 'loaded') {
      return this.state.videoCategoryById[id];
    }

    return Promise.resolve(this.state.videoCategoryById[id]);
  }

  async resetState(): Promise<void> {
    this.refreshVideoCategoriesState();
    this.state.videoCategoryById = {};
    this.state.videoCategoryByIdRequestState = {};
    this.videoCategoryByIdPromise = {};
  }

  async complete(id: number, valid: boolean, videoCategoryId?: number, skillId?: number): Promise<void> {
    return BackEndService.sendPostRequest(`videos/${id}/complete`, { valid }).then((response: Video) => {
      if(videoCategoryId) {
        this.refreshVideoCategoriesState();
        this.state.videoCategoryById[videoCategoryId].videos = this.state.videoCategoryById[videoCategoryId].videos?.map(video => {
          if (video.id === id) {
            return response;
          }
          return video;
        })
      }
      else {
       this.resetState();

       if(skillId) {
        skillService.state.skillDetails[skillId].videos = skillService.state.skillDetails[skillId].videos?.map(video => {
          if (video.id === id) {
            return response;
          }
          return video;
        })
       }
      }

    })
  }
}

export const videoService = new VideoService();